import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  PieChart,
  Lock,
  LineChart,
  Shield,
  Wallet,
  BarChart3,
  Check,
  TrendingUp,
  Bell,
  Menu,
  MapPin,
  Home,
  PiggyBank,
  Briefcase,
} from 'lucide-react';
import SEO from '../components/SEO';
import { Button } from '@/components/ui/button';
import HoitoLogo from '@/components/HoitoLogo';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

export default function Landing() {
  const [navOpen, setNavOpen] = useState(false);
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased font-sans">
      <SEO
        title="Hoito - UK Personal Finance & Wealth Management"
        description="The personal finance platform built for UK savers and investors. Track assets, ISAs, pensions, and net worth. Free forever."
        keywords="UK personal finance, wealth management UK, ISA tracker, pension calculator, net worth UK, mortgage calculator UK"
      />

      {/* Navigation - light primary */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <HoitoLogo showText={true} href="/" />
            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
                How it works
              </a>
              <a href="#features" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
                Features
              </a>
              <Link to="/tools" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
                Tools
              </Link>
              <Link to="/login" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
                Sign in
              </Link>
              <Button asChild size="lg" className="rounded-full bg-slate-900 text-white hover:bg-slate-800 font-semibold h-11 px-6 shrink-0 btn-hover-lift">
                <Link to="/register">Get started free</Link>
              </Button>
            </div>
            {/* Mobile nav */}
            <Sheet open={navOpen} onOpenChange={setNavOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden -mr-2 hover:bg-slate-100 transition-colors duration-200">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <div className="flex flex-col gap-1 pt-2">
                  <a href="#how-it-works" onClick={() => setNavOpen(false)} className="py-3 text-slate-700 hover:text-slate-900 font-medium">
                    How it works
                  </a>
                  <a href="#features" onClick={() => setNavOpen(false)} className="py-3 text-slate-700 hover:text-slate-900 font-medium">
                    Features
                  </a>
                  <Link to="/tools" onClick={() => setNavOpen(false)} className="py-3 text-slate-700 hover:text-slate-900 font-medium">
                    Tools
                  </Link>
                  <Link to="/login" onClick={() => setNavOpen(false)} className="py-3 text-slate-700 hover:text-slate-900 font-medium">
                    Sign in
                  </Link>
                  <Button asChild className="mt-4 rounded-full bg-slate-900 text-white hover:bg-slate-800 font-semibold btn-hover-lift">
                    <Link to="/register" onClick={() => setNavOpen(false)}>Get started free</Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* Hero - white primary, fills first viewport */}
      <section className="min-h-screen flex flex-col justify-center pt-14 pb-8 sm:pt-16 sm:pb-10 lg:pt-20 lg:pb-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-16 xl:gap-20 items-center">
            <div className="order-2 lg:order-1 text-center lg:text-left">
              {/* Badges */}
              <div className="mb-5 sm:mb-6 flex flex-wrap justify-center lg:justify-start gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold bg-slate-900 text-white shadow-sm">
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  Built for the UK
                </span>
                <span className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                  Free forever
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-[2.85rem] font-bold text-slate-900 leading-[1.1] tracking-tight">
                Your money, finally in one place
              </h1>
              <p className="mt-5 sm:mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Whether you&apos;re saving for a house, planning for retirement, or just want to stop losing track of your ISAs and pensions – Hoito helps you see exactly where you stand.
              </p>
              {/* What you can track */}
              <div className="mt-7 sm:mt-8 flex flex-wrap justify-center lg:justify-start gap-5 sm:gap-6">
                <div className="flex items-center gap-2 text-slate-600">
                  <Home className="w-4 h-4 sm:w-5 sm:h-5 text-slate-900 shrink-0" />
                  <span className="text-sm sm:text-base">Property & mortgages</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-slate-900 shrink-0" />
                  <span className="text-sm sm:text-base">ISAs & pensions</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <PiggyBank className="w-4 h-4 sm:w-5 sm:h-5 text-slate-900 shrink-0" />
                  <span className="text-sm sm:text-base">Savings & investments</span>
                </div>
              </div>
              <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row justify-center lg:justify-start gap-3">
                <Button asChild size="lg" className="rounded-full bg-slate-900 text-white hover:bg-slate-800 font-semibold h-12 px-7 gap-2 btn-hover-lift w-full sm:w-auto">
                  <Link to="/register">
                    Get started free
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 font-medium h-12 btn-hover-lift-sm w-full sm:w-auto">
                  <Link to="/tools">Try free calculators</Link>
                </Button>
              </div>
            </div>
            {/* Hero visual: human image + floating cards */}
            <div className="relative flex justify-center lg:block w-full order-1 lg:order-2 min-h-[220px] sm:min-h-[280px] md:min-h-[340px] lg:min-h-[400px] xl:min-h-[460px]">
              {/* Person image in circular frame */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-[180px] h-[180px] sm:w-[240px] sm:h-[240px] md:w-[320px] md:h-[320px] lg:w-[380px] lg:h-[380px] xl:w-[420px] xl:h-[420px] shrink-0 rounded-full overflow-hidden bg-slate-200 ring-4 ring-slate-100">
                  <img
                    src="/hero-person.jpg"
                    alt="Person managing finances with Hoito"
                    className="block w-full h-full object-cover"
                    loading="eager"
                    onError={(e) => {
                      const target = e.currentTarget;
                      target.style.display = 'none';
                      const fallback = target.parentElement?.querySelector('.hero-image-fallback');
                      if (fallback) (fallback as HTMLElement).style.display = 'flex';
                    }}
                  />
                  <div
                    className="hero-image-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-slate-300 to-slate-200"
                    aria-hidden="true"
                  >
                    <svg className="w-32 h-32 text-slate-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </div>
                </div>
              </div>
              {/* Floating notification card - hidden on small screens */}
              <div className="hidden md:block absolute top-2 right-0 lg:top-4 w-[160px] lg:w-[200px] rounded-xl bg-white border border-slate-200 shadow-lg p-3 sm:p-3.5 animate-float">
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-slate-900 flex items-center justify-center shrink-0">
                    <Bell className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-900">Net worth updated</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">Synced 5m ago</p>
                  </div>
                </div>
              </div>
              {/* Floating asset allocation card - hidden on small screens */}
              <div className="hidden md:block absolute bottom-4 lg:bottom-8 left-0 w-[160px] lg:w-[200px] rounded-xl bg-white border border-slate-200 shadow-lg p-3 sm:p-3.5 animate-float [animation-delay:1.5s]">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-900">Asset allocation</span>
                  <TrendingUp className="w-3.5 h-3.5 text-slate-600" />
                </div>
                <div className="mt-1.5 flex gap-1.5">
                  <div className="flex-1 h-1.5 rounded-full bg-slate-900" />
                  <div className="flex-1 h-1.5 rounded-full bg-slate-300" />
                  <div className="flex-1 h-1.5 rounded-full bg-slate-200" />
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">Stocks, real estate, cash</p>
              </div>
              {/* Net worth snapshot card - hidden on small screens */}
              <div className="hidden md:block absolute top-1/2 -left-2 lg:left-0 -translate-y-1/2 w-[150px] lg:w-[180px] rounded-xl bg-white border border-slate-200 shadow-lg p-3 sm:p-3.5">
                <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Net Worth</p>
                <p className="text-xl font-semibold text-slate-900">£248,500</p>
                <p className="text-[11px] text-slate-600 mt-0.5">+12% this month</p>
              </div>
            </div>
          </div>
          {/* Trust badges at bottom of hero */}
          <div className="mt-8 sm:mt-10 flex flex-wrap justify-center gap-2 sm:gap-3">
            <span className="inline-flex items-center px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[11px] sm:text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
              UK-focused
            </span>
            <span className="inline-flex items-center px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[11px] sm:text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
              12+ financial tools
            </span>
            <span className="inline-flex items-center px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[11px] sm:text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
              No credit card
            </span>
            <span className="inline-flex items-center px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[11px] sm:text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
              Bank-grade security
            </span>
          </div>
        </div>
      </section>

      {/* How it works - Steps */}
      <section id="how-it-works" className="pt-16 pb-16 sm:pt-20 sm:pb-20 lg:pt-24 lg:pb-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              Get started in minutes, save hours every week
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600">
              Hoito connects your UK financial picture in one place. Set up quickly and let it do the heavy lifting.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            <div className="relative group">
              <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-200/80 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300/80">
                <span className="inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-slate-100 text-slate-700 font-medium text-sm">1</span>
                <h3 className="mt-4 sm:mt-5 text-base sm:text-lg font-semibold text-slate-900">Add your assets and liabilities</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                  Property, ISAs, pensions, mortgages, savings. Add what you own and what you owe in a few taps.
                </p>
              </div>
            </div>
            <div className="relative group">
              <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-200/80 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300/80">
                <span className="inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-slate-100 text-slate-700 font-medium text-sm">2</span>
                <h3 className="mt-4 sm:mt-5 text-base sm:text-lg font-semibold text-slate-900">See your net worth at a glance</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                  Your dashboard updates automatically. Charts, projections, and breakdowns so you always know where you stand.
                </p>
              </div>
            </div>
            <div className="relative group">
              <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-200/80 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300/80">
                <span className="inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-slate-100 text-slate-700 font-medium text-sm">3</span>
                <h3 className="mt-4 sm:mt-5 text-base sm:text-lg font-semibold text-slate-900">Plan with confidence</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                  UK-focused calculators for mortgages, retirement, emergency funds, and capital gains. Make decisions backed by numbers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              Why UK savers choose Hoito
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600">
              The straightforward platform built for UK personal finance.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="flex gap-3 sm:gap-4 p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-slate-200/80 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300/80">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-slate-100 flex items-center justify-center">
                <PieChart className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-slate-900">Every payment and holding tracked</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                  See what you own, what you owe, and how it changes over time. Net worth, asset allocation, and 10-year projections in one dashboard.
                </p>
              </div>
            </div>

            <div className="flex gap-3 sm:gap-4 p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-slate-200/80 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300/80">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-slate-100 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900">Built-in planning tools</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                  UK mortgage calculator, retirement planner, emergency fund, debt payoff, and capital gains. Professional-grade tools, free to use.
                </p>
              </div>
            </div>

            <div className="flex gap-3 sm:gap-4 p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-slate-200/80 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300/80">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-slate-100 flex items-center justify-center">
                <LineChart className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900">All your data in one place</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                  No more spreadsheets or sticky notes. Assets, liabilities, investments, and transactions in a single view.
                </p>
              </div>
            </div>

            <div className="flex gap-3 sm:gap-4 p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-slate-200/80 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300/80">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-slate-100 flex items-center justify-center">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900">Built for trust</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                  Your data stays yours. Bank-grade security, clear privacy, and role-based access. We never share without your permission.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust / Security */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              Your data, secure
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600">
              We take security and privacy seriously. Your data is stored securely and compliant with UK regulations.
            </p>
          </div>

          <div className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                <Lock className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="mt-4 font-semibold text-slate-900">Encrypted storage</h3>
              <p className="mt-2 text-sm text-slate-600">
                Your financial data is encrypted at rest and in transit.
              </p>
            </div>
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                <Shield className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="mt-4 font-semibold text-slate-900">Straightforward privacy</h3>
              <p className="mt-2 text-sm text-slate-600">
                Simple terms. Your data stays with you. We never sell it.
              </p>
            </div>
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                <Wallet className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="mt-4 font-semibold text-slate-900">You own it</h3>
              <p className="mt-2 text-sm text-slate-600">
                Export anytime. Your data is yours to control.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl sm:rounded-3xl bg-slate-900 px-6 py-12 sm:px-10 sm:py-16 lg:px-16 lg:py-20 text-center transition-all duration-200 hover:shadow-xl">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">
              Start your financial journey
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-xl mx-auto">
              Free to get started. Built for UK savers. No credit card required. Cancel anytime.
            </p>
            <div className="mt-10">
              <Button asChild size="lg" className="rounded-full bg-white text-slate-900 hover:bg-slate-100 font-semibold h-12 px-8 gap-2 btn-hover-lift">
                <Link to="/register">
                  Get started free
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-400">
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                Free forever plan
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                No hidden fees
              </span>
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                Setup in minutes
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - white primary */}
      <footer className="border-t border-slate-200 py-12 sm:py-14 lg:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12">
            <div>
              <HoitoLogo href="/" showText={true} />
              <p className="mt-4 text-sm text-slate-600">
                Your personal wealth management companion. Built for the UK.
              </p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-slate-900 uppercase tracking-wider">Product</h4>
              <ul className="mt-4 space-y-3">
                <li><a href="#features" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Features</a></li>
                <li><Link to="/tools" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Tools</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-slate-900 uppercase tracking-wider">Company</h4>
              <ul className="mt-4 space-y-3">
                <li><Link to="/about" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">About</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-slate-900 uppercase tracking-wider">Legal</h4>
              <ul className="mt-4 space-y-3">
                <li><Link to="/privacy" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-500">© {new Date().getFullYear()} Hoito. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
